import React from 'react';

/**
 * Modern High-Fidelity UI Graphic & Visual Preview Component
 * Renders authentic, realistic dashboard mockups, workflow visualizations,
 * and tech interfaces instead of wireframe placeholders.
 */
export const PlaceholderImage = ({
  width = 600,
  height = 360,
  title = 'System Architecture',
  category = 'Technology',
  theme = 'orange', // 'orange' | 'blue' | 'purple' | 'cyan'
  className = '',
  style = {}
}) => {
  const themes = {
    orange: {
      primary: '#ea580c',
      secondary: '#f97316',
      accent: '#fb923c',
      subtle: 'rgba(234, 88, 12, 0.12)',
      border: 'rgba(234, 88, 12, 0.3)',
      pillBg: 'rgba(234, 88, 12, 0.2)'
    },
    blue: {
      primary: '#2563eb',
      secondary: '#3b82f6',
      accent: '#60a5fa',
      subtle: 'rgba(37, 99, 235, 0.12)',
      border: 'rgba(37, 99, 235, 0.3)',
      pillBg: 'rgba(37, 99, 235, 0.2)'
    },
    purple: {
      primary: '#7c3aed',
      secondary: '#8b5cf6',
      accent: '#a78bfa',
      subtle: 'rgba(124, 58, 237, 0.12)',
      border: 'rgba(124, 58, 237, 0.3)',
      pillBg: 'rgba(124, 58, 237, 0.2)'
    },
    cyan: {
      primary: '#0891b2',
      secondary: '#06b6d4',
      accent: '#22d3ee',
      subtle: 'rgba(8, 145, 178, 0.12)',
      border: 'rgba(8, 145, 178, 0.3)',
      pillBg: 'rgba(8, 145, 178, 0.2)'
    }
  };

  const currentTheme = themes[theme] || themes.orange;
  const isMarketing = theme === 'orange' || category.toLowerCase().includes('marketing') || category.toLowerCase().includes('roas');
  const isAutomation = theme === 'blue' || category.toLowerCase().includes('automation') || category.toLowerCase().includes('crm');

  return (
    <div
      className={`ui-preview-graphic rounded-4 overflow-hidden position-relative ${className}`}
      style={{
        background: 'linear-gradient(145deg, #0d1117 0%, #161b22 100%)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 12px 36px rgba(0, 0, 0, 0.25)',
        minHeight: `${Math.min(height, 280)}px`,
        color: '#f0f6fc',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '1.25rem',
        ...style
      }}
    >
      {/* Top Bar: macOS-style controls + Section badge */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ff5f56' }} />
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ffbd2e' }} />
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#27c93f' }} />
          <span className="ms-2 text-muted font-monospace" style={{ fontSize: '0.75rem' }}>
            {isMarketing ? 'campaign-analytics.msquare' : isAutomation ? 'crm-workflow-engine' : 'api.msquare.internal'}
          </span>
        </div>
        <span
          className="badge rounded-pill fw-semibold font-monospace"
          style={{
            backgroundColor: currentTheme.pillBg,
            color: currentTheme.accent,
            border: `1px solid ${currentTheme.border}`,
            fontSize: '0.72rem',
            letterSpacing: '0.04em'
          }}
        >
          {category.toUpperCase()}
        </span>
      </div>

      {/* Middle Interactive / Visual Mockup Content */}
      <div className="flex-grow-1 d-flex flex-column justify-content-center py-2">
        {isMarketing ? (
          /* Realistic Performance Marketing Dashboard */
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div>
                <span className="text-secondary small d-block">Campaign Performance</span>
                <strong className="fs-5 text-white">{title}</strong>
              </div>
              <div className="text-end">
                <span className="badge bg-success-subtle text-success small">● Active</span>
              </div>
            </div>

            {/* KPI Cards Row */}
            <div className="row g-2 mt-1">
              <div className="col-4">
                <div className="p-2 rounded-3" style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <span className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>ROAS</span>
                  <span className="fw-bold text-white fs-6">4.2x</span>
                </div>
              </div>
              <div className="col-4">
                <div className="p-2 rounded-3" style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <span className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Conversions</span>
                  <span className="fw-bold text-white fs-6">1,840</span>
                </div>
              </div>
              <div className="col-4">
                <div className="p-2 rounded-3" style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <span className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>CAC</span>
                  <span className="fw-bold text-white fs-6">-42%</span>
                </div>
              </div>
            </div>

            {/* Visual Progress / Trend Bar */}
            <div className="mt-3">
              <div className="d-flex justify-content-between small text-muted mb-1" style={{ fontSize: '0.72rem' }}>
                <span>Funnel Attribution</span>
                <span style={{ color: currentTheme.accent }}>98.4% Tracked</span>
              </div>
              <div className="progress" style={{ height: 6, backgroundColor: 'rgba(255, 255, 255, 0.08)' }}>
                <div
                  className="progress-bar rounded"
                  style={{ width: '84%', backgroundColor: currentTheme.secondary }}
                  role="progressbar"
                />
              </div>
            </div>
          </div>
        ) : isAutomation ? (
          /* Realistic Automation Flow Visualization */
          <div>
            <div className="mb-2">
              <span className="text-secondary small d-block">Automated Pipeline</span>
              <strong className="fs-5 text-white">{title}</strong>
            </div>

            {/* Workflow steps */}
            <div className="d-flex flex-column gap-2 mt-2">
              <div
                className="d-flex align-items-center justify-content-between p-2 rounded-3"
                style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.06)', fontSize: '0.8rem' }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span style={{ color: currentTheme.accent }}>⚡</span>
                  <span>1. New Inquiry Received</span>
                </div>
                <span className="badge bg-success-subtle text-success" style={{ fontSize: '0.68rem' }}>Triggered</span>
              </div>

              <div
                className="d-flex align-items-center justify-content-between p-2 rounded-3"
                style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.06)', fontSize: '0.8rem' }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span style={{ color: currentTheme.accent }}>💬</span>
                  <span>2. WhatsApp & SMS Notification</span>
                </div>
                <span className="text-muted" style={{ fontSize: '0.68rem' }}>&lt; 2s delay</span>
              </div>

              <div
                className="d-flex align-items-center justify-content-between p-2 rounded-3"
                style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.06)', fontSize: '0.8rem' }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span style={{ color: currentTheme.accent }}>📊</span>
                  <span>3. CRM Record & Auto Assignment</span>
                </div>
                <span className="badge bg-primary-subtle text-primary" style={{ fontSize: '0.68rem' }}>Completed</span>
              </div>
            </div>
          </div>
        ) : (
          /* Realistic Tech / Software Console Preview */
          <div>
            <div className="mb-2">
              <span className="text-secondary small d-block">Software & API Infrastructure</span>
              <strong className="fs-5 text-white">{title}</strong>
            </div>

            <div
              className="p-3 rounded-3 font-monospace mt-2"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                fontSize: '0.76rem',
                lineHeight: '1.6'
              }}
            >
              <div className="text-muted">{'// Production Endpoint Health'}</div>
              <div className="text-success">POST /api/v1/sync → 200 OK (38ms)</div>
              <div style={{ color: currentTheme.accent }}>✓ Secure SSL &bull; Auto-Scaling Active</div>
              <div className="text-muted">Uptime: 99.98% | Database: Synchronized</div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Status strip */}
      <div
        className="d-flex align-items-center justify-content-between pt-2 mt-2"
        style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', fontSize: '0.72rem', color: '#8b949e' }}
      >
        <span>MSquare Production System</span>
        <span className="d-flex align-items-center gap-1">
          <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#238636' }} />
          Verified Architecture
        </span>
      </div>
    </div>
  );
};

export default PlaceholderImage;
